'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQ {
  question: string;
  answer: string;
}

export default function FAQSection() {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(0);

  const faqs: FAQ[] = [
    {
      question: 'Can I change my plan at any time?',
      answer:
        'Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately, and billing is prorated based on your usage. If you upgrade, you will be charged the difference for the remainder of the billing period. If you downgrade, credits will be applied to your next invoice.',
    },
    {
      question: 'What payment methods do you accept?',
      answer:
        'We accept all major credit cards (Visa, Mastercard, American Express), PayPal, and bank transfers for enterprise customers. For annual plans, we also offer invoice-based payment options with NET 30 terms for qualified businesses.',
    },
    {
      question: 'Is there a free trial available?',
      answer:
        'Yes, we offer a 14-day free trial for the Professional plan with no credit card required. This gives you full access to all Professional features so you can evaluate the platform thoroughly before committing. Enterprise customers can request an extended trial period.',
    },
    {
      question: 'What happens if I exceed my plan limits?',
      answer:
        'If you approach your plan limits, we will notify you via email. You can either upgrade to a higher tier or purchase additional resources as add-ons. We provide a grace period to prevent service interruption, and overage charges are clearly communicated in advance.',
    },
    {
      question: 'Do you offer discounts for annual billing?',
      answer:
        'Yes, annual billing provides a 20% discount compared to monthly billing across all plans. Enterprise customers may qualify for additional volume discounts based on their specific requirements and commitment terms.',
    },
    {
      question: 'What is included in enterprise support?',
      answer:
        'Enterprise support includes 24/7 priority assistance with 1-hour response time, a dedicated account manager, quarterly business reviews, custom onboarding and training, direct access to our engineering team, and proactive monitoring of your infrastructure.',
    },
    {
      question: 'Can I get a custom plan for my organization?',
      answer:
        'Absolutely. For organizations with unique requirements, we offer fully customized plans with tailored features, pricing, and support levels. Contact our sales team to discuss your specific needs and receive a personalized proposal.',
    },
    {
      question: 'What is your refund policy?',
      answer:
        'We offer a 30-day money-back guarantee for all new subscriptions. If you are not satisfied with our service within the first 30 days, contact our support team for a full refund. This policy applies to both monthly and annual plans.',
    },
  ];

  const toggleFAQ = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <div data-component-id="src/app/(public)/pricing-plans/components/FAQSection.tsx:62:4" data-component-path="src/app/(public)/pricing-plans/components/FAQSection.tsx" data-component-line="62" data-component-end-line="99" data-component-file="FAQSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-3%22%7D" className="space-y-3">
      {faqs.map((faq, index) => (
        <div data-component-id="src/app/(public)/pricing-plans/components/FAQSection.tsx:64:8" data-component-path="src/app/(public)/pricing-plans/components/FAQSection.tsx" data-component-line="64" data-component-end-line="97" data-component-file="FAQSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
          key={index}
          className={`backdrop-blur-xl rounded-2xl border overflow-hidden transition-all duration-300 ${
            expandedIndex === index
              ? 'bg-[#1B365D]/30 border-[#0EA5E9]/30 shadow-lg shadow-[#0EA5E9]/10'
              : 'bg-[#1B365D]/20 border-white/10 hover:border-[#0EA5E9]/25'
          }`}
        >
          <button data-component-id="src/app/(public)/pricing-plans/components/FAQSection.tsx:72:10" data-component-path="src/app/(public)/pricing-plans/components/FAQSection.tsx" data-component-line="72" data-component-end-line="91" data-component-file="FAQSection.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-full%20px-6%20py-5%20flex%20items-center%20justify-between%20text-left%22%7D"
            onClick={() => toggleFAQ(index)}
            className="w-full px-6 py-5 flex items-center justify-between text-left"
            aria-expanded={expandedIndex === index}
          >
            <span data-component-id="src/app/(public)/pricing-plans/components/FAQSection.tsx:77:12" data-component-path="src/app/(public)/pricing-plans/components/FAQSection.tsx" data-component-line="77" data-component-end-line="77" data-component-file="FAQSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-base%20font-semibold%20text-white%20pr-4%22%7D" className="text-base font-semibold text-white pr-4">{faq.question}</span>
            <div data-component-id="src/app/(public)/pricing-plans/components/FAQSection.tsx:78:12" data-component-path="src/app/(public)/pricing-plans/components/FAQSection.tsx" data-component-line="78" data-component-end-line="90" data-component-file="FAQSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
              className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                expandedIndex === index
                  ? 'bg-[#0EA5E9]/20 text-[#0EA5E9]'
                  : 'bg-white/5 text-white/40'
              }`}
            >
              <Icon data-component-id="src/app/(public)/pricing-plans/components/FAQSection.tsx:85:14" data-component-path="src/app/(public)/pricing-plans/components/FAQSection.tsx" data-component-line="85" data-component-end-line="89" data-component-file="FAQSection.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ChevronDownIcon%22%7D"
                name="ChevronDownIcon"
                size={16}
                className={`transition-transform duration-300 ${expandedIndex === index ? 'rotate-180' : ''}`}
              />
            </div>
          </button>
          {expandedIndex === index && (
            <div data-component-id="src/app/(public)/pricing-plans/components/FAQSection.tsx:93:12" data-component-path="src/app/(public)/pricing-plans/components/FAQSection.tsx" data-component-line="93" data-component-end-line="95" data-component-file="FAQSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22px-6%20pb-5%20pt-1%20border-t%20border-white%2F10%22%7D" className="px-6 pb-5 pt-1 border-t border-white/10">
              <p data-component-id="src/app/(public)/pricing-plans/components/FAQSection.tsx:94:14" data-component-path="src/app/(public)/pricing-plans/components/FAQSection.tsx" data-component-line="94" data-component-end-line="94" data-component-file="FAQSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-white%2F65%20leading-relaxed%22%7D" className="text-sm text-white/65 leading-relaxed">{faq.answer}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
